import { Urls } from "oc-commons/Urls";
import * as Mimetypes from "oc-commons/Mimetypes";
import { I18N } from "oc-I18N";
import * as Commons from "oc-commons/Commons";
import * as Dialogs from "oc-ui/dialogs/Dialogs";
import _ from "lodash";

export interface IConfigLoadOptions {
  code?: string | null;
  metamodell?: boolean;
  i18n?: boolean;
  settings?: boolean;
  metadata?: boolean;
  tableMetaTags?: string | null;
  tenant?: string | null;
  lang?: string | null;
}

export interface IWkfg2TypeConverter {
  parse(metadata: any): any;
  parseSettings(settings: any, source: any): void;
  format(table: string, metadata: any): any;
}

export interface IFileEntry {
  fileName: string;
  blob: Blob;
}

export type MetadataLoadMapper = (params: {
  metadata: any;
  metadataPlain: any;
}) => Promise<any> | any;

/**
 * Kapselt die Low-Level-Rest-API zum WKFG2 - Backend. Die Methoden geben jeweils Promises zurück.
 *
 * Für die meisten Zwecke innerhalb der APP sollte man statt dessen die Wkfg2BackendExtension verwenden,
 * weil dort ganze Workflows wie das Speichern einer Konfiguration mit Erzeugen aller Snapshots sowie die
 * APP-Events abgebildet sind.
 *
 * Typischer Aufruf:
 *
 * <pre><code>
 * const backendApi = new Wkfg2BackendApi(APP.settings.wkfgBackendUrl);
 * backendApi.loadMetadata().then((metadata)=>{
 *     console.log('Das komplette Produktmodell:',metadata);
 * });
 * </code></pre>
 *
 * Falls der Ajax-Aufruf fehlschlägt, wird defaultmäßig ein Fehlerdialog angezeigt.
 * Falls man statt dessen eine eigene Fehlerbehandlung durchführen möchte, benutzt man die Promise-catch-Methode:
 *
 * <pre><code>
 * const backendApi = new Wkfg2BackendApi(APP.settings.wkfgBackendUrl);
 * backendApi.loadConfiguration('id=1').then(configuration=>{
 *    console.log(configuration);
 * }).catch(reason=>{
 *    console.log('Den Fehler behandele ich selbst und zeige keinen Fehlerdialog an! Fehler: ',reason);
 * });
 * </code></pre>
 *
 * Das Angeben einer Default-Fehleraktion und normales Debuggen von Fehlern innerhalb von then/catch-Methoden
 * ist kein Standard-Verhalten von Promises, sondern eine holodeck-Erweiterung. Siehe dazu oc-commons/Promise.
 */

export class Wkfg2BackendApi {
  wkfgBackendUrl: string;
  wkfgTypeConverter: IWkfg2TypeConverter | undefined;
  metadataLoadMapper: MetadataLoadMapper;

  /**
   * Creates an instance of Wkfg2BackendApi.
   * @param {string} wkfgBackendUrl
   * @param {?Wkfg2TypeConverter} wkfgTypeConverter
   * @param {?MetadataLoadMapper} falls gesetzt, wird dieser statt des wkfgTypeConverter zum Laden des metadata verwendet
   */
  constructor(
    wkfgBackendUrl: string,
    wkfgTypeConverter?: IWkfg2TypeConverter,
    metadataLoadMapper: MetadataLoadMapper = (
      { metadata }, // removed metadataPlain from destructuring as unused in default
    ) => (this.wkfgTypeConverter ? this.wkfgTypeConverter.parse(metadata) : metadata),
  ) {
    this.wkfgBackendUrl = wkfgBackendUrl;
    this.wkfgTypeConverter = wkfgTypeConverter;
    this.metadataLoadMapper = metadataLoadMapper;
  }

  /**
   * Lädt die Beschreibung des Metamodells und der Metadaten in einem, und gibt ein Promise zurück.
   * Hinweis: Best Practise ist es, die I18N-Texte nicht über diesen Weg, sondern direkt als Teil der HTML-Seite auszuliefern,
   * damit z.B. der Loading-Screen oder Fehlermeldungen in der richtigen Sprache erscheinen.
   * @returns {Promise} {
   *    Konfiguration: {
   *        id: 1,
   *        Code: "MBN443QTX",
   *        ...
   *    },
   *    metadata: {
   *        MaterialPbr: [
   *          {
   *              id: 1,
   *              name: "Leder",
   *              ...
   *          },
   *          ...
   *        ]
   *    },
   *    metamodell: {
   *       languagesI18N: ["de","en"],
   *       tables: [
   *          {
   *              name: "Konfiguration",
   *              label: "Konfigurationen",
   *              ...
   *          },
   *          ...
   *       }
   *    },
   *    i18n_json: {
   *       "__shortname": "de",
   *       "number_dec_point": ",",
   *       "3 pro Seite": "3 pro Seite",
   *       ...
   *    },
   *    content_url: 'http://localhost/wp-content/uploads/wkfgcontent',
   * }
   */
  loadConfigData({
    code = null,
    metamodell = true,
    i18n = true,
    settings = true,
    metadata = true,
    tableMetaTags = null,
    tenant = null,
    lang = null,
  }: IConfigLoadOptions = {}): Promise<any> {
    const urlObject = new URL(this.wkfgBackendUrl);
    urlObject.pathname = urlObject.pathname + "metamodell/config";
    if (code) urlObject.searchParams.set("Code", code);
    urlObject.searchParams.set("metamodel", String(metamodell));
    urlObject.searchParams.set("metadata", String(metadata));
    urlObject.searchParams.set("settings", String(settings));
    urlObject.searchParams.set("i18n", String(i18n));
    if (!Commons.isEmpty(tableMetaTags)) {
      urlObject.searchParams.set("tableMetaTags", tableMetaTags!);
    }
    const actualUrl = new URL(document.location.href);

    // Explicit typing for array to help inference
    (
      [
        ["tenant", tenant],
        ["lang", lang],
      ] as [string, string | null | undefined][]
    ).forEach(([key, paramValue]) => {
      let value = paramValue;
      if (Commons.isEmpty(value)) {
        value = actualUrl.searchParams.get(key);
      }
      if (!Commons.isEmpty(value)) {
        urlObject.searchParams.set(key, value!);
      }
    });
    const url = urlObject.toString();
    return this.fetchJson(url).then(async (config: any) => {
      if (config.i18n_json) {
        I18N.setKeyValueMap(config.i18n_json);
      }
      I18N.hasMissing = config.i18n_hasMissing === true;
      APP.metamodelPlain = _.cloneDeep(config.metamodel);
      config.metadataPlain = APP.metadataPlain = _.cloneDeep(config.metadata);
      APP.metamodel = config.metamodel;
      this.allowMovedTabelsToBeAccessed(config.metadata);
      config.metadata = await this.metadataLoadMapper({
        metadata: config.metadata,
        metadataPlain: config.metadataPlain,
      });

      if (config.settings) {
        Commons.defaultsDeepSafe(APP.settings, config.settings);
      }
      APP.settingsPlain = _.cloneDeep(APP.settings);

      if (this.wkfgTypeConverter) {
        if (APP.metamodel?.settings) {
          // Diese Settings kommen aus base.metamodell.js ganz unten, diese dürfen die Settings aus global_js_constants nicht überschreiben (darauf muss man selbst achten!)!
          this.wkfgTypeConverter.parseSettings(APP.settings, APP.metamodel.settings);
        }
        if (config.metadata?.Settings) {
          // Das sind die Settings aus der Backend-Tabelle Settings, auch diese dürfen die Settings aus global_js_constants nicht überschreiben (darauf muss man selbst achten!)!
          this.wkfgTypeConverter.parseSettings(APP.settings, config.metadata.Settings);
        }
      }

      return config;
    });
  }

  /**
   * Lädt die Beschreibung des Metamodells - also den Inhalt der metamodel.json, und gibt ein Promise zurück
   * @returns {Promise} Promise mit dem Inhalt der JSON-Datei
   */
  loadMetamodel(): Promise<any> {
    const url = this.wkfgBackendUrl + "metamodell/json";
    return this.fetchJson(url);
  }

  /**
   * Lädt die Metadata (meist die relationalen Daten der Produkte, Materialien, etc.)
   * @param {string} [query='tableMetaTags=Produktmodell']
   * @returns {Promise} Promise mit den Produkttabellen im Format {metadata: {"TableName": [Entries]}}
   */
  loadMetadata(
    query = "tableMetaTags=Produktmodell",
  ): Promise<{ metadata: any; metadataPlain: any }> {
    let url = Urls.addQsPart(this.wkfgBackendUrl + "metamodell/query", query);
    const lang = Urls.urlParam("lang");
    if (lang) {
      url = Urls.addOrUpdateQsParameter(url, "lang", lang);
    }
    const promise = this.fetchJson(url);
    if (this.wkfgTypeConverter) {
      return promise.then((metadata: any) => {
        const metadataPlain = _.cloneDeep(metadata);
        this.wkfgTypeConverter!.parse(metadata);
        this.allowMovedTabelsToBeAccessed(metadata);
        return { metadata, metadataPlain };
      });
    }
    return promise.then((metadata: any) => {
      return { metadata, metadataPlain: _.cloneDeep(metadata) };
    });
  }

  allowMovedTabelsToBeAccessed(metadata: any): void {
    if (APP.metamodel) {
      const existingNames = new Set(APP.metamodel.tables.map((t: any) => t.name));
      APP.metamodel.tables.forEach((t: any) =>
        t.oldNames
          ?.filter((o: any) => !existingNames.has(o))
          .forEach((o: string) => {
            metadata[o] = metadata[t.name]; //arrays are passed by referenz
          }),
      );
    }
  }

  /**
   * Lädt eine Configuration vom Backend. configurationQuery ist dabei der Query-URL-Teil, der an
   * das Konfiguration-Backend-API weitergeleitet wird
   * @param {string} query
   * @returns {Promise} Promise mit der Konfiguration als Objekt
   */
  loadConfiguration(query: string): Promise<any> {
    const fullQuery = Urls.addQsPart("table=Konfiguration&limit=1", query);
    // The original code `_.get(data, "Konfiguration[0]")` would likely fail if `data` is `{ metadata: ..., metadataPlain: ... } `.
    // Assuming "Konfiguration" table data is nested under the `metadata` property.
    return this.loadMetadata(fullQuery).then((data) => _.get(data.metadata, "Konfiguration[0]"));
  }

  /**
   * Speichert ein einzelnes Metadata-Objekt
   * @param {string} table wie "Konfiguration"
   * @param {string} action kann sein 'create' oder 'update'
   * @param {Object} metadata das zu speichernde Metadaten-Objekt
   * @param {*} [language=I18N.getLanguage()] optional die Sprache, für die das Metadata-Objekt gespeichert wird
   * @returns {Promise} Promise mit dem Metadaten-Objekt mit vom Server geupdateten Werten (wie id, created_at, updated_at)
   */
  saveMetadata(
    table: string,
    action: "create" | "update",
    metadata: any,
    language: string = I18N.getLanguage(),
  ): Promise<any> {
    const url = this.wkfgBackendUrl + `metamodell/${action}/${table}/${language} `;
    const metadataFormatted =
      this.wkfgTypeConverter == null ? metadata : this.wkfgTypeConverter.format(table, metadata);
    return this.fetchJson(url, "post", metadataFormatted);
  }

  /**
   * Speichert globale Settings (key-value-Object) auf dem Server - geht natürlich nur als Admin.
   * Die Settings sollten nur die Settings enthalten, die man überschreiben möchte.
   * Die Settings werden als Table erwartet, wie im Backend angezeigt.
   * Abweichend von saveMetadata werden allerdings nicht die id verwendet, um eventuell vorhandene Einträge zu überschreiben, sondern der Key.
   * Beispiel:
   * saveSettings([{Key:'stage.renderer.isSRGBConversion',Value:'false',Type:'String',Comment:'Sollen Bilder/Farben beim Laden nach SRGB konvertiert werden?'}]);
   * @param {Array<{Key:string,Value:string,Type:string,Comment:string}>}
   */
  saveSettings(settings: any[]): Promise<any> {
    const url = this.wkfgBackendUrl + `metamodell/saveSettings`;
    return this.fetchJson(url, "post", settings);
  }

  updatePrices(data: any): Promise<any> {
    const url = this.wkfgBackendUrl + "price/calculate";
    return this.fetchJson(url, "post", data);
  }

  /**
   * Löscht ein einzelnes Metadata-Objekt mitsamt Content
   * @param {string} table wie "Konfiguration"
   * @param {(number|string)} id
   * @param {boolean} [withContent=true] optional true oder false
   * @returns {Promise} Promise mit Text "ok"
   */
  deleteMetadata(table: string, id: number | string, withContent: boolean = true): Promise<any> {
    const url = this.wkfgBackendUrl + `metamodell/delete/${table}/${id}/${withContent}`;
    return this.fetchJson(url, "post");
  }

  /**
   * Leert eine ganze Metadata-Tabelle mitsamt Content
   * @param {string} table wie "Konfiguration"
   * @param {boolean} [withContent=true] optional true oder false
   * @returns {Promise} Promise mit Text "ok"
   */
  truncateMetadata(table: string, withContent: boolean = true): Promise<any> {
    const url = this.wkfgBackendUrl + `metamodell/truncate/${table}/${withContent}`;
    return this.fetchJson(url, "post");
  }

  /**
   * Lädt ein oder mehrere Content-Dateien hoch. Die entsprechenden ContentUploadPatterns müssen im metamodel.json
   * eingetragen sein.
   * @param {string} path das Content-Verzeichnisses (Wurzel ist wkfgcontent), in das Hochgeladen werden soll
   * @param {(Array<{fileName:string,blob:Blob}|File>|{fileName:string,blob:Blob}|File)} files ein {blob,fileName} oder File - Objekt, oder ein Array davon
   * @param {string} [url=this.wkfgBackendUrl + 'content/upload']
   * @returns {Promise} Promise mit der Liste der tatsächlich gespeicherten Server-Dateinamen (die Dateinamen werden normalisiert)
   * @example
   * STAGE.renderToBlob('image/jpg',(blob)=>{
   *     backendApi
   *         .uploadContent(`Konfiguration/${Code}/`, [{fileName:'Vorderansicht.jpg',blob}])
   *         .then(realName=>console.log(realName))
   * });
   */
  uploadContent(
    path: string,
    files: IFileEntry | File | (IFileEntry | File)[],
    url: string = this.wkfgBackendUrl + "content/upload",
  ): Promise<any> {
    const formData = new FormData();
    formData.append("path", path);
    (_.isArrayLike(files)
      ? (files as (IFileEntry | File)[])
      : [files as IFileEntry | File]
    ).forEach((entry) => {
      if (entry instanceof File) {
        formData.append("file[]", entry, entry.name);
      } else {
        formData.append("file[]", entry.blob, entry.fileName);
      }
    });
    return this.fetchJson(url, "post", formData);
  }

  /**
   * Verwendet von APP.augmentedReality
   * Lädt eine GLB mit Projektparameter hoch
   * @param {string} path
   * @param {(Array<{fileName:string,blob:Blob}|File>|{fileName:string,blob:Blob}|File)} files
   * @param {String} project
   * @param {Boolean} convert
   * @returns {Promise}
   */
  augmentedRealityAPI(
    path: string,
    files: IFileEntry | File | (IFileEntry | File)[],
    project: string,
    convert: boolean,
  ): Promise<any> {
    const url = this.wkfgBackendUrl + "content/augmentedReality";
    const formData = new FormData();
    formData.append("path", path);
    formData.append("project", project);
    formData.append("convert", String(convert));
    (_.isArrayLike(files)
      ? (files as (IFileEntry | File)[])
      : [files as IFileEntry | File]
    ).forEach((entry) => {
      if (entry instanceof File) {
        formData.append("file", entry, entry.name);
      } else {
        formData.append("file", entry.blob, entry.fileName);
      }
    });
    return this.fetchJson(url, "post", formData);
  }

  /**
   * Verwendet von APP.augmentedReality
   * Lädt eine GLB mit Projektparameter hoch
   * @param {string} path
   * @param {(Array<{fileName:string,blob:Blob}|File>|{fileName:string,blob:Blob}|File)} files
   * @param {String} project
   * @param {Number} multipart
   * @param {Number} part
   * @param {Boolean} useRenderfarm
   * @param {String} blendFilename
   * @returns {Promise}
   */
  fotorealisticRenderAPI(
    path: string,
    files: IFileEntry | File | (IFileEntry | File)[],
    project: string,
    multipart: number,
    part: number,
    useRenderfarm: boolean,
    blendFilename: string,
  ): Promise<any> {
    const url = this.wkfgBackendUrl + "content/fotorealisticRender";
    const formData = new FormData();
    formData.append("path", path);
    formData.append("project", project);
    formData.append("multipart", String(multipart));
    formData.append("part", String(part));
    formData.append("useRenderfarm", String(useRenderfarm));
    formData.append("blendFilename", blendFilename);
    const fileList = _.isArrayLike(files)
      ? (files as (IFileEntry | File)[])
      : [files as IFileEntry | File];
    fileList.forEach((entry) => {
      if (entry instanceof File) {
        formData.append("file", entry, entry.name);
      } else {
        formData.append("file", entry.blob, entry.fileName);
      }
    });
    return this.fetchJson(url, "post", formData);
  }

  /**
   * Lädt ein canvasElement als Bild zum Server hoch.
   * Ein Image kann mittels der Images.canvasFromImage Methode in ein Canvas überführt werden
   * @param canvasElement
   * @param {string} path wie "Textures/Holz.jpg"
   * @returns {Promise}
   */
  uploadCanvasImage(canvasElement: HTMLCanvasElement, path: string): Promise<any> {
    const mimetype = Mimetypes.getMimetypeOfFilename(path) as string;
    return new Promise((resolve, reject) => {
      canvasElement.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas toBlob failed"));
          return;
        }
        const uploadPath = (path as any).substringBeforeLast("/");
        const fileName = (path as any).substringAfterLast("/");
        this.uploadContent(uploadPath, { fileName, blob }).then(resolve).catch(reject);
      }, mimetype);
    });
  }

  /**
   * Listet die Dateien eines Content-Verzeichnisses auf
   * @param {string} path Pfad des Content-Verzeichnisses (Wurzel ist wkfgcontent)
   * @returns {Promise}
   */
  listContent(path: string): Promise<any> {
    let url = Urls.addOrUpdateQsParameter(
      this.wkfgBackendUrl + "content/list",
      "path",
      (path as any).ensureRight("/"), // String ext?
    );
    // Note: .ensureRight is likely a string extension in this project.
    return this.fetchJson(url, "get");
  }

  /**
   * Löscht eine einzelne Content-Datei
   * @param {string} path Pfad der Content-Datei (Wurzel ist wkfgcontent)
   * @returns {Promise}
   */
  deleteContent(path: string): Promise<any> {
    const url = this.wkfgBackendUrl + "content/delete";
    let formData = new FormData();
    formData.append("path", path);
    return this.fetchJson(url, "post", formData);
  }

  /**
   * Gibt die URL eines PDFs zu einer Konfiguration zurück. Das PDF wird beim Aufruf der URL serverseitig erzeugt.
   * @param {(string|number)} codeOrId Code oder id der Konfiguration
   * @param {boolean} [inline=false] optional: true das PDF wird im Browser angezeigt, false (default) es wird als Datei heruntergeladen
   * @returns {string}
   */
  getPdfUrl(codeOrId: string | number, inline: boolean = false): string {
    let urlString = `${this.wkfgBackendUrl}pdf/${codeOrId}`;
    // The original code `url.param("inline", true)` would fail if `url` is a string.
    // Assuming `param` was intended to add a query parameter, using `Urls.addOrUpdateQsParameter`.
    if (inline) {
      urlString = Urls.addOrUpdateQsParameter(urlString, "inline", "true");
    }
    return urlString;
  }

  /**
   * sendet eine Mail mit dem PDF an die in der Konfiguration angegebene Mailadresse
   * @returns {Promise}
   */
  sendKonfigurationMail(): Promise<any> {
    const url = Urls.addOrUpdateQsParameter(
      this.wkfgBackendUrl + "mail",
      "Code",
      APP.configuration.Code,
    );
    return this.fetchJson(url);
  }

  /**
   *
   * @param {string} url
   * @param {string} method
   * @param {FormData|Object|string} body
   * @returns {Promise}
   */
  fetchJson(url: string, method: string = "get", body: any = undefined): Promise<any> {
    if (_.isPlainObject(body)) {
      body = JSON.stringify(body);
    }
    const contentType = "application/json"; //body instanceof FormData ? 'multipart/form-data' : 'application/json';

    let headers: HeadersInit = Object.assign({}, APP.getAjaxHeaders());

    if (!(body instanceof FormData)) {
      headers["Accept"] = "application/json";
      headers["Content-Type"] = "application/json";
    }

    return Dialogs.defaultCatchAsWarning(
      fetch(url, {
        method,
        body,
        headers,
      }).then((response) => {
        if (!response.ok) {
          throw new Error("Failed with HTTP code " + response.status);
        }
        return response.json();
      }),
    );
  }
}
